import type { BodyUUID, Quaternion, Vector3 } from "../../types/scene";
import { create } from "mutative";
import { logger } from "../../utils/logger";
import type { SceneStoreGetState, SceneStoreSetState } from "../sceneStore.types";
import type { BodyActions } from "./actionTypes";
import { withHistory } from "./withHistory";
import { sanitizeQuaternion, sanitizeVector3, type WorldBodyRef } from "./bodyActionUtils";

export const createBodyFieldActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): Pick<
  BodyActions,
  | "setBodyName"
  | "setWorldBodyDisplayName"
  | "setBodyGravityCompensation"
  | "setBodyQuaternion"
  | "setBodyTransform"
  | "getBodyPosition"
  | "getBodyRotation"
> => ({
  setBodyName: withHistory((bodyUuid: BodyUUID, name: string) => {
    set((state) =>
      create(state, (draft) => {
        const body = draft.bodies?.[bodyUuid] || null;
        if (!body) return;
        body.name = name;
      })
    );
  }),

  setWorldBodyDisplayName: withHistory((rootBodyUuid: BodyUUID, name: string) => {
    set((state) =>
      create(state, (draft) => {
        const list = (draft.worldBodies || []) as WorldBodyRef[];
        const idx = list.findIndex((wb) => {
          const uuid = typeof wb === "string" ? wb : wb?.uuid;
          return uuid === rootBodyUuid;
        });
        if (idx < 0) {
          logger.warn("[setWorldBodyDisplayName] root not in worldBodies", { rootBodyUuid });
          return;
        }
        list[idx] = { uuid: rootBodyUuid, name };
      })
    );
  }),

  setBodyGravityCompensation: withHistory((bodyUuid: BodyUUID, value: number | undefined) => {
    set((state) =>
      create(state, (draft) => {
        const body = draft.bodies?.[bodyUuid] || null;
        if (!body) return;
        if (value === undefined || value === 0) {
          delete body.gravityCompensation;
        } else {
          body.gravityCompensation = value;
        }
      })
    );
  }),

  setBodyQuaternion: withHistory((bodyUuid: BodyUUID, quaternion: Quaternion) => {
    const safeQuat = sanitizeQuaternion(quaternion);
    set((state) =>
      create(state, (draft) => {
        const body = draft.bodies[bodyUuid] || null;
        if (body) {
          body.orientation = safeQuat;

          // Store by UUID for O(1) lookup
          if (!draft.bodyRotations) draft.bodyRotations = {};
          draft.bodyRotations[bodyUuid] = [...safeQuat];

          // Dispatch event to notify UI components of transform updates
          try {
            window.dispatchEvent(
              new CustomEvent("body-transform-updated", {
                detail: { bodyUuid, type: "rotation", value: safeQuat },
              })
            );
          } catch (error) {
            logger.warn("[bodyActions] Failed to dispatch rotation body-transform-updated", error);
          }
        }
      })
    );
  }),

  // Commit both position and rotation in a single history entry
  setBodyTransform: withHistory((bodyUuid: BodyUUID, position: Vector3, quaternion: Quaternion) => {
    const safePos = sanitizeVector3(position);
    const safeQuat = sanitizeQuaternion(quaternion);
    set((state) =>
      create(state, (draft) => {
        const body = draft.bodies[bodyUuid] || null;
        if (body) {
          body.position = safePos;
          body.orientation = safeQuat;

          // Store by UUID for O(1) lookup
          if (!draft.bodyPositions) draft.bodyPositions = {};
          if (!draft.bodyRotations) draft.bodyRotations = {};
          draft.bodyPositions[bodyUuid] = [...safePos];
          draft.bodyRotations[bodyUuid] = [...safeQuat];

          // Dispatch event to notify UI components of transform updates
          try {
            window.dispatchEvent(
              new CustomEvent("body-transform-updated", {
                detail: { bodyUuid, type: "transform", position: safePos, quaternion: safeQuat },
              })
            );
          } catch (error) {
            logger.warn("[bodyActions] Failed to dispatch transform body-transform-updated", error);
          }
        }
      })
    );
  }),
  getBodyPosition: (bodyUuid: BodyUUID): Vector3 => {
    const state = get();
    const bodyMap = state?.bodies || {};
    const body = bodyMap[bodyUuid] || null;
    return body?.position || [0, 0, 0];
  },

  getBodyRotation: (bodyUuid: BodyUUID): Quaternion => {
    const state = get();
    const bodyMap = state?.bodies || {};
    const body = bodyMap[bodyUuid] || null;
    return body?.orientation || [1, 0, 0, 0];
  },
});
